import {
    FRAGMENT_LISTING_DISCOUNT,
    FRAGMENT_LISTING_FACETS,
    FRAGMENT_LISTING_VARIANT,
    FRAGMENT_STOREFRONT_BRANDCARD,
    FRAGMENT_STOREFRONT_EDGES,
    FRAGMENT_STOREFRONT_EDGES_LIGHT,
} from '@marketplace/graphql/config/shared-fragments.js'
import { FRAGMENT_LISTING_PRODUCT_CARD } from '@marketplace/graphql/fragments/listing.js'
import gql from 'graphql-tag'

// Caution: when modifying this query, if you add a facets parameter the listing search event breaks.
export const DYNAMICALLY_FILTERED_PRODUCTS_QUERY = gql`
    ${FRAGMENT_LISTING_DISCOUNT}
    query dynamicallyFilteredProductsQuery(
        $categoryPath: String
        $collectionSlug: String
        $country: [CountryCode]
        $curatedFilters: JSON
        $curatedStorefrontFilters: JSON
        $discounts: Boolean
        $isOwner: Boolean
        $leadTimeMax: [NumericRange]
        $minimumOrder: MinimumOrderAmount
        $margin: Float = 0
        $msrpMax: [NumericRange]
        $msrpMin: [NumericRange]
        $newProducts: DateRange
        $notSoldOnMarketplaces: Boolean
        $bestsellers: Boolean
        $perPage: Int!
        $query: String
        $setPage: Int
        $shipsTo: CountryCode
        $sellsTo: RetailerType
        $sort: ListingSearchSort!
        $storefrontIds: [ID]
        $tags: [ListingTag]
        $odfDiscountFrom: Float
        $madeIn: [CountryCode]
        $isActiveTica: Boolean
        $inStock: Boolean
    ) {
        listingSearch(
            first: $perPage
            from: $setPage
            sort: $sort
            query: $query
            storefront: {
                ids: $storefrontIds
                shipsTo: [$shipsTo]
                sellsTo: $sellsTo
                country: $country
                minimumOrderAmount: $minimumOrder
                filters: $curatedStorefrontFilters
                isPublished: $isOwner
                campaignDiscountPercentage: $odfDiscountFrom
            }
            listing: {
                isSoldOnThirdPartyMarketplaces: $notSoldOnMarketplaces
                collectionSlugs: [$collectionSlug]
                categoryPaths: [$categoryPath]
                filters: $curatedFilters
                tags: $tags
                leadTimeMax: $leadTimeMax
                publishedAt: $newProducts
                hasDiscounts: $discounts
                madeIn: $madeIn
                margin: $margin
                msrpMin: $msrpMin
                msrpMax: $msrpMax
                isPublished: $isOwner
                isBestseller: $bestsellers
                isActiveTica: $isActiveTica
                inStock: $inStock
            }
        ) {
            seoInfo {
                attributes {
                    key
                    value
                }
                isCustom
                metaDescription
                metaTitle
                path
                seoDescription
                title
            }
            listingSearchResults {
                listing {
                    ...FragmentGlobalDiscount
                    availableAt
                    brand
                    caseQuantity
                    minimumOrderQuantity
                    databaseId
                    id
                    inStock
                    isBestseller
                    isFavorite
                    isNew
                    isTopPick
                    msrpMax
                    msrpMin
                    priceMax
                    priceMin
                    title
                    description
                    translation {
                        description
                    }
                    url
                    loyaltyDiscount {
                        percentage
                        campaign
                        orderchampContributionPercentage
                    }
                    images(first: 3) {
                        totalCount
                        edges {
                            node {
                                id
                                x1: transformedUrl(width: 480, height: 480)
                                x2: transformedUrl(width: 800, height: 800)
                                transformedUrl(
                                    width: 1200
                                    height: 1200
                                    method: FILL
                                )
                            }
                        }
                    }
                    storefront {
                        slug
                        name
                        shippingCountries
                        campaignDiscount {
                            id
                            campaign
                            percentage
                            createdAt
                            orderchampContributionPercentage
                        }
                        discountPercentage
                        needsRetailerApproval
                        retailerApprovalStatus
                        databaseId
                    }
                    # Test if we can remove this
                    # variants(first: 1) {
                    #     totalCount
                    #     edges {
                    #         node {
                    #             options {
                    #                 name
                    #                 value
                    #             }
                    #             id
                    #             inventory
                    #             inventoryPolicy
                    #             price
                    #             msrp
                    #         }
                    #     }
                    # }
                }
            }
            pageInfo {
                __typename
                hasNextPage
                hasPreviousPage
                totalCount
            }
        }
    }
`

export const DYNAMICALLY_FILTERED_STOREFRONT_PRODUCTS_FACETS_QUERY = gql`
    query dynamicallyFilteredStorefrontProductsFacetsQuery(
        $categoryPath: String
        $collectionSlug: String
        $country: [CountryCode]
        $curatedFilters: JSON
        $curatedStorefrontFilters: JSON
        $discounts: Boolean
        $isOwner: Boolean
        $leadTimeMax: [NumericRange]
        $minimumOrder: MinimumOrderAmount
        $msrpMax: [NumericRange]
        $msrpMin: [NumericRange]
        $newProducts: DateRange
        $notSoldOnMarketplaces: Boolean
        $bestsellers: Boolean
        $perPage: Int!
        $query: String
        $setPage: Int
        $shipsTo: CountryCode
        $sellsTo: RetailerType
        $sort: ListingSearchSort!
        $storefrontIds: [ID]
        $tags: [ListingTag]
        $odfDiscountFrom: Float
    ) {
        listingFacets: listingSearch(
            first: $perPage
            from: $setPage
            sort: $sort
            query: $query
            storefront: {
                ids: $storefrontIds
                shipsTo: [$shipsTo]
                sellsTo: $sellsTo
                country: $country
                minimumOrderAmount: $minimumOrder
                filters: $curatedStorefrontFilters
                isPublished: $isOwner
                campaignDiscountPercentage: $odfDiscountFrom
            }
            listing: {
                isSoldOnThirdPartyMarketplaces: $notSoldOnMarketplaces
                collectionSlugs: [$collectionSlug]
                categoryPaths: [$categoryPath]
                filters: $curatedFilters
                tags: $tags
                leadTimeMax: $leadTimeMax
                publishedAt: $newProducts
                hasDiscounts: $discounts
                msrpMin: $msrpMin
                msrpMax: $msrpMax
                isPublished: $isOwner
                isBestseller: $bestsellers
            }
            facets: [
                LISTING_CATEGORY_FACET
                LISTING_STOREFRONT_EXTRA_FILTER_FACET
                LISTING_FILTER_FACET
                LISTING_STOREFRONT_FILTER_FACET
                LISTING_TAGS_FACET
                LISTING_COLLECTION_FACET
                LISTING_EXTRA_FILTER_FACET
                LISTING_STOREFRONT_COUNTRY_FACET
                LISTING_STOREFRONT_MINIMUM_ORDER_AMOUNT_FACET
                LISTING_MSRP_PRICE_FACET
            ]
        ) {
            facets {
                __typename
                ...FragmentListingFacets
                ... on ListingCollectionFacet {
                    __typename
                    id
                    inputName
                    label
                    values {
                        id
                        label
                        count
                    }
                }
            }
        }
    }
    ${FRAGMENT_LISTING_FACETS}
`

export const STOREFRONT_MORE_FROM_BRAND_BY_SLUG_QUERY = gql`
    ${FRAGMENT_LISTING_PRODUCT_CARD}
    query storefrontMoreFromBrandBySlugQuery($slug: String!, $count: Int!) {
        storefront: storefrontBySlug(slug: $slug) {
            id
            databaseId
            isFollowing
            name
            slug
            tags
            description
            translation {
                description
            }
            minimumOrderAmount
            avatar: storyImage {
                transformedUrl(width: 128, height: 128, method: CROP)
            }
            header: headerImage {
                transformedUrl(width: 1060, height: 320)
            }
            listings(first: $count, sort: UPDATED_AT_DESC) {
                edges {
                    node {
                        ...FragmentListingProductCard
                    }
                }
            }
        }
    }
`

export const STOREFRONT_LISTING_BY_SLUG_QUERY = gql`
    query storefrontListingBySlugQuery($slug: String!, $listingId: ID!) {
        storefrontBySlug(slug: $slug) {
            isReturningStorefrontCustomer
            analyticTags
            campaignDiscount {
                id
                campaign
                percentage
                createdAt
                minimumOrderValue
                ends_at
                orderchampContributionPercentage
            }
            loyaltyDiscount {
                campaign
                percentage
                orderchampContributionPercentage
            }
            orderDiscounts {
                id
                name
                minimumOrderValue
                percentage
                campaign
            }
            discountPercentage
            id
            databaseId
            name
            slug
            status
            isOwner
            isActiveTica
            avatar: storyImage {
                transformedUrl(width: 128, height: 128, method: CROP)
            }
            shippingCountries
            shippingSurchargePrice
            shippingFreeAbove
            orderDiscounts {
                percentage
                minimumOrderValue
                campaign
            }
            country
            minimumOrderAmountReturningCustomer
            minimumOrderAmount
            reviewScore
            realAverageReviewScore
            reviews(first: 1) {
                totalCount
            }
            tags
            doesntSellTo
            needsRetailerApproval
            retailerApprovalStatus
            isHotDeal
            listing(id: $listingId) {
                databaseId
                analyticTags
                inStock
                hasShippingSurcharge
                id
                url
                isFavorite
                title
                description
                aiDescription {
                    original
                    translation
                }
                translation {
                    description
                }
                currency
                variantsCount
                caseQuantity
                minimumOrderQuantity
                surchargeGroup {
                    name
                    allowStackingSetting
                    countries {
                        id
                        countries
                        surcharge_amount
                    }
                }
                availableAt
                leadTime
                madeIn
                filterValues {
                    attribute
                    filterText
                    valueText
                    translation {
                        filterText
                        valueText
                    }
                }
                customFields {
                    name
                    value
                    type
                    label
                }
                tags
                width
                length
                height
                weight
                msrpMin
                isReorderOnly
                images(first: 25) {
                    totalCount
                    pageInfo {
                        hasNextPage
                        endCursor
                        startCursor
                    }
                    edges {
                        node {
                            id
                            modal: transformedUrl(
                                width: 400
                                height: 400
                                method: FILL
                            )
                            small: transformedUrl(
                                width: 700
                                height: 700
                                method: FILL
                            )
                            big: transformedUrl(
                                width: 1200
                                height: 1200
                                method: FILL
                            )
                        }
                    }
                }
                mainImageDescription
                variants {
                    id
                    databaseId
                    inventory
                    inventoryPolicy
                    options {
                        name
                        value
                    }
                    price
                    msrp
                    image {
                        id
                    }
                }
                categories(first: 1, sort: DEPTH_DESC) {
                    edges {
                        node {
                            name
                            slug
                            path
                        }
                    }
                }
                globalDiscount {
                    percentage
                    campaign
                    ends_at
                    minimumQuantity
                    customerGroupType
                    isComposite
                    orderchampContributionPercentage
                }
                bulkDiscounts {
                    ends_at
                    percentage
                    customerGroupType
                    percentage
                    databaseId
                    minimumQuantity
                    orderchampContributionPercentage
                }
                specialDiscount {
                    percentage
                    customerGroupType
                    minimumQuantity
                }
                loyaltyDiscount {
                    percentage
                    campaign
                    ends_at
                    orderchampContributionPercentage
                }
                reviews(first: 1) {
                    totalCount
                }
                gpsrManufacturerInformation
                gpsrSafetyInformation
                gpsrBatchCode
            }
            isStarSeller
            isBestPrice
            externalLinks {
                provider {
                    name
                    externalUrlParameters
                }
                externalUrl
                internalUrl
                locale
            }
        }
    }
`

export const STOREFRONT_LISTING_GET_MORE_IMAGES = gql`
    query storefrontListingGetMoreImagesQuery(
        $slug: String!
        $listingId: ID!
        $after: String!
    ) {
        storefrontBySlug(slug: $slug) {
            id
            listing(id: $listingId) {
                id
                images(first: 50, after: $after) {
                    edges {
                        node {
                            id
                            modal: transformedUrl(
                                width: 200
                                height: 200
                                method: CROP
                            )
                            small: transformedUrl(
                                width: 700
                                height: 700
                                method: FILL
                            )
                            big: transformedUrl(
                                width: 1200
                                height: 1200
                                method: FILL
                            )
                        }
                    }
                }
            }
        }
    }
`

export const STOREFRONT_LISTING_VARIANTS_BY_SLUG_QUERY = gql`
    query storefrontVariantsBySlugQuery($slug: String!, $listingId: ID!) {
        variants: storefrontBySlug(slug: $slug) {
            id
            listing(id: $listingId) {
                id
                aiDescription {
                    original
                    translation
                }
                variants {
                    id
                    databaseId
                    inventory
                    inventoryPolicy
                    options {
                        name
                        value
                    }
                    width
                    height
                    length
                    weight
                    diameter
                    volume
                    barcode
                    sku
                    price
                    msrp
                    leadTime
                    image {
                        id
                    }
                    allergens
                    ingredients
                    customFields {
                        name
                        value
                        type
                        label
                    }
                }
            }
        }
    }
`

export const STOREFRONT_BY_SLUG_HEADER_QUERY = gql`
    query storefrontBySlugHeaderQuery($slug: String!) {
        storefrontBySlug(slug: $slug) {
            isReturningStorefrontCustomer
            slug
            analyticTags
            city
            country
            currency
            databaseId
            discountPercentage
            description
            doesntSellTo
            facebookFollowers
            facebookUsername
            hasFreeShipping
            hasPayAfterArrival
            hasAutomaticSortingEnabled
            headerImage {
                transformedUrl(width: 2000, height: 400)
            }
            tileImage {
                transformedUrl(width: 300, height: 300, method: CROP)
            }
            id
            instagramFollowers
            instagramUsername
            isFollowing
            isPublished
            isOwner
            firstPublishedAt
            publishedAt
            primaryCountry
            leadTime
            minimumOrderAmount
            minimumReorderAmount
            minimumOrderAmountReturningCustomer
            name
            reviews(first: 1) {
                totalCount
            }
            reviewScore
            realAverageReviewScore
            shippingCountries
            shippingFreeAbove
            storyImage {
                transformedUrl(width: 400, height: 400, method: CROP)
            }
            translation {
                description
            }
            vacationModeEnabled
            vacationModeEndedAt
            vacationModeStartedAt
            campaignDiscount {
                id
                campaign
                percentage
                createdAt
                ends_at
                orderchampContributionPercentage
            }
            isStarSeller
            isActiveTica
            isOutlet
            hasDiscoveryDaysMov
            isBestPrice
            isReorderDaysParticipant
            isLowMinimumsParticipant
            status
            carouselImages {
                id
                large: transformedUrl(width: 800, height: 800)
                small: transformedUrl(width: 400, height: 400, method: CROP)
            }
            listingsCount
            isHotDeal
            categories {
                id
                slug
                path
            }
            productCollections(first: 50) {
                edges {
                    node {
                        id
                        name
                        slug
                        images(first: 1) {
                            edges {
                                node {
                                    transformedUrl(
                                        width: 200
                                        height: 200
                                        method: CROP
                                    )
                                }
                            }
                        }
                    }
                }
                pageInfo {
                    hasNextPage
                }
            }
            externalLinks {
                provider {
                    name
                    externalUrlParameters
                }
                externalUrl
                internalUrl
                locale
            }
            relatedStorefronts {
                id
                slug
                name
                storyImage {
                    transformedUrl(width: 128, height: 128, method: CROP)
                }
            }
        }
    }
`

export const STOREFRONT_ALTERNATIVES_BY_SLUG_QUERY = gql`
    query storefrontAlternativesBySlugQuery($slug: String!) {
        storefrontBySlug(slug: $slug) {
            id
            alternativeStorefronts {
                ...FragmentStorefrontBrandCard
                storyImage {
                    transformedUrl(width: 256, height: 256, method: CROP)
                }
            }
        }
    }
    ${FRAGMENT_STOREFRONT_BRANDCARD}
`

export const STOREFRONT_ESSENTIAL_DATA = gql`
    query storefrontEssentialDataQuery($slug: String!) {
        storefront: storefrontBySlug(slug: $slug) {
            city
            country
            databaseId
            description
            id
            isFollowing
            isPublished
            name
            slug
            isStarSeller
            translation {
                description
            }
            carouselImages {
                id
                large: transformedUrl(width: 800, height: 800)
                small: transformedUrl(width: 400, height: 400, method: CROP)
            }
            headerImage {
                transformedUrl(width: 2000, height: 400)
            }
            storyImage {
                transformedUrl(width: 400, height: 400, method: CROP)
            }
            tileImage {
                transformedUrl(width: 400, height: 400, method: CROP)
            }
        }
    }
`

export const STOREFRONT_REVIEWS_DATA = gql`
    query storefrontReviewsDataQuery($slug: String!) {
        reviews: storefrontBySlug(slug: $slug) {
            id
            reviewScore
            realAverageReviewScore
            reviews(first: 1) {
                totalCount
            }
        }
    }
`

export const STOREFRONT_GUEST_LISTINGS = gql`
    ${FRAGMENT_LISTING_DISCOUNT}
    query storefrontGuestListingsQuery(
        $id: [ID]!
        $count: Int!
        $categoryPath: String
    ) {
        listingSearch(
            first: $count
            from: 0
            sort: OPTIMISED
            facets: [LISTING_CATEGORY_FACET]
            storefront: { ids: $id }
            listing: { categoryPaths: [$categoryPath] }
        ) {
            listingSearchResults {
                listing {
                    ...FragmentGlobalDiscount
                    id
                    title
                    description
                    translation {
                        description
                    }
                    brand
                    priceMin
                    priceMax
                    msrpMin
                    msrpMax
                    inStock
                    availableAt
                    isFavorite
                    isBestseller
                    isNew
                    databaseId
                    url
                    caseQuantity
                    images(first: 3) {
                        totalCount
                        edges {
                            node {
                                id
                                x1: transformedUrl(width: 480, height: 480)
                                x2: transformedUrl(width: 800, height: 800)
                                transformedUrl(
                                    width: 1200
                                    height: 1200
                                    method: FILL
                                )
                            }
                        }
                    }
                }
            }
            pageInfo {
                totalCount
            }
            facets {
                __typename
                ... on ListingCategoryFacet {
                    __typename
                    id
                    inputName
                    label
                    values {
                        id
                        label
                        count
                    }
                }
            }
        }
    }
`

export const STOREFRONT_VACATION_QUERY = gql`
    query storefrontVacationQuery($slug: String!) {
        vacation: storefrontBySlug(slug: $slug) {
            id
            vacationModeEnabled
            vacationModeEndedAt
            vacationModeStartedAt
        }
    }
`

export const STOREFRONT_INVITE_QUERY = gql`
    query storefrontInviteQuery($slug: String!) {
        storefrontBySlug(slug: $slug) {
            id
            databaseId
            name
            slug
            city
            country
            isDropshipping
            storyImage {
                transformedUrl(width: 400, height: 400, method: CROP)
            }
            headerImage {
                transformedUrl(width: 1060, height: 320)
            }
            reviewScore
            realAverageReviewScore
        }
    }
`

export const STOREFRONT_COLLECTIONS_QUERY = gql`
    query storefrontCollectionsQuery($slug: String!, $count: Int) {
        collections: storefrontBySlug(slug: $slug) {
            id
            productCollections(first: $count) {
                edges {
                    node {
                        id
                        name
                        slug
                    }
                }
                pageInfo {
                    hasNextPage
                }
            }
        }
    }
`

export const COLLECTION_BY_SLUG_QUERY = gql`
    query collectionBySlugQuery(
        $slug: String!
        $storefront: String!
        $width: Int!
        $height: Int!
        $method: ImageResizeMethod!
        $count: Int!
    ) {
        collection: collectionBySlug(slug: $slug, storefront: $storefront) {
            id
            name
            description
            translation {
                description
            }
            slug
            images(first: $count) {
                edges {
                    node {
                        transformedUrl(
                            width: $width
                            height: $height
                            method: $method
                        )
                    }
                }
            }
        }
    }
`

export const STOREFRONT_REVIEW_QUERY = gql`
    ${FRAGMENT_LISTING_VARIANT}
    query storefrontReviewsQuery($slug: String!, $count: Int!, $after: String) {
        reviews: storefrontBySlug(slug: $slug) {
            id
            reviewScore
            realAverageReviewScore
            allreviews: reviews(first: 1) {
                totalCount
            }
            reviews(first: $count, after: $after, sort: ID_DESC) {
                totalCount
                pageInfo {
                    endCursor
                    startCursor
                    hasNextPage
                    hasPreviousPage
                }
                edges {
                    node {
                        id
                        score
                        reply
                        content
                        translation {
                            content
                        }
                        createdAt
                        reviewProducts(first: 15) {
                            totalCount
                            edges {
                                node {
                                    id
                                    listing {
                                        id
                                        databaseId
                                        isPublished
                                        image {
                                            id
                                            transformedUrl(
                                                width: 200
                                                height: 200
                                                method: CROP
                                            )
                                        }
                                        title
                                    }
                                    listingVariantId
                                    listingVariant {
                                        ...FragmentListingVariant
                                    }
                                }
                            }
                        }
                        retailer {
                            id
                            city
                            country
                            user {
                                firstName
                            }
                            preferences {
                                storeType
                            }
                        }
                    }
                }
            }
        }
    }
`

export const PRODUCT_REVIEW_QUERY = gql`
    ${FRAGMENT_LISTING_VARIANT}
    query productReviewsQuery(
        $slug: String!
        $count: Int!
        $listingId: ID!
        $after: String
    ) {
        reviews: storefrontBySlug(slug: $slug) {
            id
            reviewScore
            realAverageReviewScore
            reviews(first: 1) {
                totalCount
            }
            listing(id: $listingId) {
                id
                reviews(first: $count, after: $after, sort: ID_DESC) {
                    totalCount
                    pageInfo {
                        endCursor
                        startCursor
                        hasNextPage
                        hasPreviousPage
                    }
                    edges {
                        node {
                            score
                            reply
                            id
                            content
                            translation {
                                content
                            }
                            createdAt
                            reviewProducts(first: 5) {
                                edges {
                                    node {
                                        id
                                        listing {
                                            id
                                            databaseId
                                            isPublished
                                            image {
                                                id
                                                transformedUrl(
                                                    width: 200
                                                    height: 200
                                                    method: CROP
                                                )
                                            }
                                            title
                                        }
                                        listingVariantId
                                        listingVariant {
                                            ...FragmentListingVariant
                                        }
                                    }
                                }
                            }
                            retailer {
                                id
                                city
                                country
                                user {
                                    firstName
                                }
                                preferences {
                                    storeType
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export const FOLLOW_STOREFRONT_MUTATION = gql`
    mutation FollowStorefront($storefrontId: ID!) {
        followStorefront(input: { storefrontId: $storefrontId }) {
            clientMutationId
            success
        }
    }
`

export const UNFOLLOW_STOREFRONT_MUTATION = gql`
    mutation UnFollowStorefront($storefrontId: ID!) {
        unFollowStorefront(input: { storefrontId: $storefrontId }) {
            clientMutationId
            success
        }
    }
`

export const STOREFRONT_TRACKING_MUTATION = gql`
    mutation storefrontTrackingMutation(
        $storefrontId: ID!
        $recommenderId: String
        $isDropshipping: Boolean = false
    ) {
        storefrontTracking(
            input: {
                storefrontId: $storefrontId
                recommenderId: $recommenderId
                isDropshipping: $isDropshipping
            }
        ) {
            clientMutationId
        }
    }
`

export const PRODUCT_GUEST_TRACKING_MUTATION = gql`
    mutation productGuestTrackingMutation(
        $listingId: ID!
        $recommenderId: String
        $isDropshipping: Boolean = false
    ) {
        listingTracking(
            input: {
                listingId: $listingId
                recommenderId: $recommenderId
                isDropshipping: $isDropshipping
            }
        ) {
            clientMutationId
        }
    }
`

export const STOREFRONT_RECENTLY_VIEWED_MUTATION = gql`
    mutation recentlyViewedStorefrontReportMutation(
        $storefrontId: ID!
        $recommenderId: String
    ) {
        recentlyViewedStorefrontReport(
            input: {
                storefrontId: $storefrontId
                recommenderId: $recommenderId
            }
        ) {
            clientMutationId
        }
    }
`

export const STOREFRONT_REFERRAL_TRACKING_MUTATION = gql`
    mutation storefrontReferralTracking($storefrontId: ID!) {
        storefrontReferralTracking(input: { storefrontId: $storefrontId }) {
            success
        }
    }
`

export const PRODUCT_TRACKING_MUTATION = gql`
    mutation productTrackingMutation($listingId: ID!, $recommenderId: String) {
        recentlyViewedListingReport(
            input: { listingId: $listingId, recommenderId: $recommenderId }
        ) {
            clientMutationId
        }
    }
`

export const REQUEST_ACCESS_APPROVAL = gql`
    mutation retailerRequestApproval(
        $storefrontId: ID!
        $url: String
        $instagramUsername: String
        $brandConcept: String
        $clientMutationId: String
    ) {
        retailerRequestApproval(
            input: {
                storefrontId: $storefrontId
                url: $url
                instagramUsername: $instagramUsername
                brandConcept: $brandConcept
                clientMutationId: $clientMutationId
            }
        ) {
            success
            clientMutationId
            userErrors {
                field
                message
            }
        }
    }
`

export const STOREFRONTS_BY_LETTER = gql`
    query storefrontsByLetterQuery($letter: String!) {
        brands: storefrontsByLetter(letter: $letter) {
            name
            slug
        }
    }
`

export const STOREFRONTS_TICA_DIRECTORY = gql`
    query ticaBrandDirectoryQuery {
        brands: allTicaStorefronts {
            id
            databaseId
            name
            slug
            status
        }
    }
`
export const STOREFRONTS_BY_FOLLOWED = gql`
    ${FRAGMENT_STOREFRONT_EDGES_LIGHT}
    query storefrontsByFollowed(
        $first: Int
        $after: String
        $status: StorefrontStatus = null
    ) {
        brands: storefronts(
            first: $first
            followed: true
            after: $after
            status: $status
        ) {
            ...FragmentStorefrontLightEdges
            pageInfo {
                endCursor
                hasNextPage
                total
            }
        }
    }
`

export const STOREFRONTS_BY_TICA_BRANDS = gql`
    ${FRAGMENT_STOREFRONT_EDGES}
    query storefrontsByTicaBrands(
        $first: Int
        $after: String
        $status: StorefrontStatus = null
    ) {
        ticaBrands: storefronts(
            first: $first
            after: $after
            boughtOnly: TICA
            status: $status
        ) {
            ...FragmentStorefrontEdges
            pageInfo {
                endCursor
                hasNextPage
                total
            }
        }
    }
`

